import { File, Query_Root } from 'src/@types/graphql'
import styled from 'styled-components'
import { S } from 'src/shared/styled/S'
import { BOX_SHADOW_ON_WHITE_BG } from 'src/shared/constants/styling-constants'
import { useClient } from 'urql'
import { useMessageSource } from 'src/i18n/useMessageSource'
import { useNotificationService } from 'src/shared/utils/NotificationService'
import { useCallback, useEffect, useState } from 'react'
import { queryKapJournalsSecondaryInfoById } from 'src/screens/kap/program/journal/kapJournalQueries'
import { FileService } from 'src/service/axios/FileService'
import { ReadOnlySelection, ReadOnlyTextField } from 'src/shared/presentation/ReadOnly'
import { HtmlRenderer } from 'src/shared/presentation/HtmlRenderer'
import { DocumentContainer, DownloadLinkStyled } from 'src/shared/presentation/FileSection'
import { DeleteIcon, DownloadIcon } from 'src/shared/icons/Icons'
import { IconButton, Typography } from '@mui/material'
import { DateUtils } from 'src/shared/utils/DateUtils'
import { DefaultSectionTypography } from 'src/shared/presentation/DefaultSectionTypography'
import { ConfirmationModalDialog } from 'src/shared/modal-dialog/ConfirmationModalDialog'
import { ENTITY_TYPE, FILE_TYPE, MODULE_TYPES } from 'src/shared/constants/constants'
import { queryFile } from 'src/shared/presentation/fileQueries'

interface Props {
  kapJournalId: number
  refetchDocs: boolean
  resetRefetchDocs: () => void
  canEdit: boolean
}

interface CardContentInfo {
  description?: string | null
  correspondence_between?: string | null
  modules?: MODULE_TYPES[]
}

const CardStyled = styled(S.Card.Container)<{ $nonClickable?: boolean }>`
  box-shadow: ${BOX_SHADOW_ON_WHITE_BG.default};

  &:first-child {
    margin-top: ${({ theme }) => theme.spacing(1)};
  }

  &:hover {
    box-shadow: ${({ $nonClickable }) =>
      $nonClickable ? BOX_SHADOW_ON_WHITE_BG.default : BOX_SHADOW_ON_WHITE_BG.hover};
  }
`

export const KapJournalCardContent = ({ kapJournalId, refetchDocs, resetRefetchDocs, canEdit }: Props) => {
  const { getMessage } = useMessageSource()

  const urqlClient = useClient()
  const notificationService = useNotificationService()

  const [cardContentInfo, setCardContentInfo] = useState<CardContentInfo>()
  const [fileUUIDToBeDeleted, setFileUUIDToBeDeleted] = useState<string>()
  const [fileDeleteConfirmationOpen, setFileDeleteConfirmationOpen] = useState(false)
  const [files, setFiles] = useState<File[]>()

  const fetchDocuments = useCallback(async () => {
    const { data } = await urqlClient
      .query<{ file: Query_Root['file'] }>(queryFile, {
        fileType: FILE_TYPE.KAP_JOURNAL,
        entityType: ENTITY_TYPE.KAP_JOURNAL,
        entityId: kapJournalId,
      })
      .toPromise()

    if (!data?.file) {
      throw new Error('Resource not found')
    }

    return data.file
  }, [kapJournalId, urqlClient])

  const fetchKapJournalsSecondaryInfo = useCallback(async () => {
    const { data } = await urqlClient
      .query<{ kap_journal_by_pk: Query_Root['kap_journal_by_pk'] }>(queryKapJournalsSecondaryInfoById, {
        id: kapJournalId,
      })
      .toPromise()

    if (!data?.kap_journal_by_pk) {
      throw new Error('Resource not found')
    }

    return data.kap_journal_by_pk
  }, [kapJournalId, urqlClient])

  const initData = useCallback(async () => {
    try {
      const journalData = await fetchKapJournalsSecondaryInfo()
      const fetchedFiles = await fetchDocuments()

      setCardContentInfo({
        description: journalData.description,
        correspondence_between: journalData.correspondence_between,
        modules: journalData.modules,
      })
      setFiles(fetchedFiles)
    } catch (err) {
      notificationService.operationFailed()
    }
  }, [fetchDocuments, fetchKapJournalsSecondaryInfo, notificationService])

  useEffect(() => {
    initData()
  }, [initData])

  useEffect(() => {
    if (refetchDocs) {
      fetchDocuments()
        .then((files) => {
          setFiles(files)
          resetRefetchDocs()
        })
        .catch((_) => notificationService.operationFailed())
    }
  }, [refetchDocs, fetchDocuments, resetRefetchDocs, notificationService])

  const handleCancelDeleteFile = () => {
    setFileUUIDToBeDeleted(undefined)
    setFileDeleteConfirmationOpen(false)
  }

  const handleDeleteFile = (fileUUID: string) => {
    setFileUUIDToBeDeleted(fileUUID)
    setFileDeleteConfirmationOpen(true)
  }

  const handleConfirmDeleteFile = async () => {
    if (fileUUIDToBeDeleted !== undefined) {
      FileService.remove(fileUUIDToBeDeleted)
        .then(() => {
          notificationService.deleteSuccessful()
          return fetchDocuments()
        })
        .then((files) => setFiles(files))
        .catch(() => {
          notificationService.operationFailed()
        })
      setFileUUIDToBeDeleted(undefined)
    }
    setFileDeleteConfirmationOpen(false)
  }

  return (
    <>
      {cardContentInfo && (
        <>
          <ReadOnlyTextField text={getMessage('label.program.journal.modules')}>
            {cardContentInfo.modules &&
              cardContentInfo.modules.map((module, index) => (
                <HtmlRenderer html={getMessage(`label.module.description.${module}`)} key={index} />
              ))}
          </ReadOnlyTextField>
          <ReadOnlyTextField text={getMessage('label.program.journal.correspondence_between')}>
            {cardContentInfo.correspondence_between && <HtmlRenderer html={cardContentInfo.correspondence_between} />}
          </ReadOnlyTextField>
          <ReadOnlyTextField text={getMessage('label.program.journal.description')}>
            {cardContentInfo.description && <HtmlRenderer html={cardContentInfo.description} />}
          </ReadOnlyTextField>
          <ReadOnlySelection text={getMessage('label.program.journal.documents')} isLast>
            {files && files.length > 0 ? (
              files.map((file) => (
                <CardStyled key={file.id}>
                  <DownloadLinkStyled href={FileService.getUrlForFileDownload(file.uuid)}>
                    <S.Card.Content>
                      <DownloadIcon fontSize="small" />
                      <DocumentContainer>
                        <Typography variant="subtitle2">{file.file_title}</Typography>
                        <Typography variant="body2" color="textSecondary">
                          ({file.file_name})
                        </Typography>
                      </DocumentContainer>
                      <Typography variant="body2" color="textSecondary">
                        {DateUtils.formatDate(DateUtils.parseDateGraphQL(file.upload_date))}
                      </Typography>
                    </S.Card.Content>
                  </DownloadLinkStyled>
                  {canEdit && (
                    <S.Card.Actions>
                      <IconButton color="primary" size="large" onClick={() => handleDeleteFile(file.uuid)}>
                        <DeleteIcon />
                      </IconButton>
                    </S.Card.Actions>
                  )}
                </CardStyled>
              ))
            ) : (
              <CardStyled $nonClickable>
                <S.Card.Content>
                  <DefaultSectionTypography noEntriesMessageKey="label.file.not.available" $standAlone={true} />
                </S.Card.Content>
              </CardStyled>
            )}
          </ReadOnlySelection>
        </>
      )}

      <ConfirmationModalDialog
        open={fileDeleteConfirmationOpen}
        onCancel={handleCancelDeleteFile}
        onConfirm={handleConfirmDeleteFile}
      >
        {getMessage('label.delete.confirm')}
      </ConfirmationModalDialog>
    </>
  )
}
